import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import Button from './Button';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  showDontShowAgain?: boolean;
  onDontShowAgainChange?: (checked: boolean) => void;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  showDontShowAgain = false,
  onDontShowAgainChange,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
         role="dialog"
         aria-modal="true"
         aria-labelledby="modal-title"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 md:p-8 bg-black/60 backdrop-blur-sm overflow-y-auto"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="relative w-full max-w-md bg-white rounded-xl shadow-2xl mx-auto my-8 sm:my-0"
          >
            <div className="p-6 sm:p-8">
              <button
               aria-label="Close modal"
                onClick={onClose}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>

              <h2 id="modal-title" className="text-xl font-semibold text-gray-800 mb-4">{title}</h2>
              
              <div className="prose prose-sm max-w-none text-gray-600">
                {children}
              </div>

              <div className="mt-6 flex flex-col space-y-4">
                {showDontShowAgain && onDontShowAgainChange && (
                  <label className="flex items-center text-sm text-gray-600">
                    <input
                     id="dont-show-again"
                      type="checkbox"
                      className="form-checkbox h-4 w-4 text-primary-600 rounded"
                      onChange={(e) => onDontShowAgainChange(e.target.checked)}
                    />
                    <span className="ml-2" htmlFor="dont-show-again">Don't show this again</span>
                  </label>
                )}

                <Button
                 aria-label="Close modal and continue"
                  variant="primary"
                  onClick={onClose}
                  fullWidth
                >
                  Got it
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Modal;